import { OmniaConfig } from "../../config"
import { IHolding } from "../../holding"
import { isExternalRole } from "../../session/utils"
import { IOmniaShell } from "../../shell"
import { RoleId } from "../../user"
import { defineDirective } from "../../utils/angular"
import { isObject } from "../../utils/utils"

type LoginStep = "email" | "login" | "tcs" | "holding"

/** @internal */
export class LoginCtrl {

    static create(): ng.IDirective {

        return defineDirective(LoginCtrl, "shared/main/login/login.html")

    }

    step: LoginStep

    email: string
    password: string

    loginError: string

    checkingEmail = false

    readonly tcs = {
        text: "",
        accept: false,
        send: false
    }

    holding: IHolding
    year: number

    invalidHolding: boolean

    static $inject = ["$scope", "$uibModal", "OmniaShell", "omniaConfig"]

    constructor(
        private readonly $scope: ng.IScope,
        private readonly $uibModal: any,
        readonly shell: IOmniaShell,
        private readonly config: OmniaConfig,
        readonly session = shell.getSession(),
        readonly brand = shell.config.brand.name,
        readonly enableSso = config.sso,
        readonly twoStepsLogin = config.sso,
        private readonly http = session.http,
        private readonly invite = session.invite) {

    }

    $onInit() {

        const { session, $scope, invite } = this

        this.email = invite?.email

        const watch = [
            () => session.currentUser,
            () => session.termsAndConditions
        ]

        $scope.$watchGroup(watch, () => this.setCurrentStep())

    }

    get loggedIn(): boolean {
        return !!this.loggedInUser
    }

    get loggedInUser() {
        return this.session.currentUser
    }

    get showHoldingSelection() {

        const { currentUser, fixedHoldingList } = this.session

        if (!currentUser) return false

        return fixedHoldingList
            ? this.session.hasFixedHoldings()
            : true

    }

    get autoSignup() {
        const { session } = this
        return session ? session.currentUserRole == RoleId.Access : false
    }

    private setCurrentStep() {

        const { session, enableSso, twoStepsLogin } = this

        if (!session.currentUser) {
            this.step = (enableSso && twoStepsLogin) ? "email" : "login"
            return
        }

        if (session.termsAndConditions?.mustAccept) {
            this.step = "tcs"
            this.tcs.text = session.termsAndConditions.tcs.text
            return
        }

        this.step = "holding"

        if (!this.holding) {
            this.holding = session.lastLoginHolding
            this.year = session.lastLoginYear
        }

    }

    canConfirm(): boolean {

        const { email, password, tcs, holding, year, checkingEmail } = this

        switch (this.step) {

            case "email":
                return Boolean(email) && (!checkingEmail)

            case "login":
                return Boolean(email && password)

            case "tcs":
                return tcs?.accept

            case "holding":
                return Boolean(holding && year)

        }

    }


    editEmail() {
        this.step = "email"
        this.email = undefined
    }

    confirm() {

        switch (this.step) {

            case "email":
                this.checkEmail()
                break

            case "login":
                this.login()
                break

            case "tcs":
                this.acceptTcs()
                break

            case "holding":
                this.selectHolding()
                break
        }

    }

    private checkEmail() {

        const { http, email, session: { invite } } = this

        let url = `/api/session/v1/login/sso/check?email=${encodeURIComponent(email)}`

        if (invite?.queryString) {
            url = `${url}&invite=${invite.queryString}`
        }

        this.checkingEmail = true

        http.get(url)
            .then(r => {

                const data = r?.data

                const link = isObject(data) ? data["loginLink"] : undefined

                if (!link) {
                    this.step = "login"
                    this.checkingEmail = false
                }
                else {
                    window.location = link
                }

            })
            .catch(_ => {

                this.step = "login"
                this.checkingEmail = false

            })

    }

    private login() {

        this.session.login({ username: this.email, password: this.password }).then(r => {

            this.loginError = r?.error

        })

    }

    private acceptTcs() {

        const { session, tcs: { send } } = this

        session.acceptTcs(session.termsAndConditions.tcs.version, send)

    }

    private selectHolding() {

        this.session.selectHolding(this.holding, this.year).then(r => {

            this.invalidHolding = !r

        })

    }


    logout() {
        this.session.logout()
    }

    canManageHoldings() {
        const { session, session: { permissions }, step } = this
        if (step !== "holding") return false
        if (isExternalRole(session)) return false
        return Boolean(permissions.holdings.write)
    }

    manageHoldings() {

        this.$uibModal.open({
            template: "<omnia-user-holdings />"
        }).result.then(() => {

            this.$scope.$broadcast("update-holdings")

        })

    }

    forgotPassword() {
        this.session.setResetPassword(true, this.email)
    }

    getHlhLoginLink() {
        const { config } = this
        return `${config.apiUrl}/login/ms/v1/authorize/omniahub`
    }

    checkLowResolution() {
        return checkLowResolution()
    }

}


function checkLowResolution() {

    let d1 = screen.availWidth
    let d2 = screen.availHeight

    if (d1 < d2) {
        let d = d1
        d1 = d2
        d2 = d
    }

    return ((d1 < 1024) || (d2 < 768))

}
